import { Link } from 'react-router-dom';
import StarRating from './StarRating';
import { PRODUCT_PLACEHOLDER_IMAGE } from '../../utils/constants';
import { getEffectiveProductPrice, extractProductThumbnail } from '../../utils/productHelpers';
import useAddToCart from '../../hooks/useAddToCart';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useTranslation } from 'react-i18next';

const ProductCard = ({
  product,
  onAddToCart,
  onToggleFavorite,
}) => {
  const { t } = useTranslation();
  const handleAddToCartDefault = useAddToCart();
  const { data: user } = useCurrentUser({ forceRefresh: false });
  const userId = user?._id || user?.id || '';

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart(product._id);
    } else {
      handleAddToCartDefault(product);
    }
  };

  const handleToggleFavorite = (isLiked) => {
    if (onToggleFavorite) {
      onToggleFavorite(product._id, isLiked);
    }
  };

  const getProductImage = () => {
    // Vignette légère (thumbnailUrls) pour les listes, fallback imageUrls.
    return extractProductThumbnail(product);
  };

  const rawPromo = product?.promoPrice;
  const promoNum =
    rawPromo !== null && rawPromo !== undefined && rawPromo !== ''
      ? Number(rawPromo)
      : NaN;
  const hasActivePromo = Number.isFinite(promoNum) && promoNum > 0;
  const displayPrice = getEffectiveProductPrice(product);
  const isLiked = Array.isArray(product?.likedBy)
    ? product.likedBy.some((id) => String(id) === String(userId))
    : false;
  const reviewCount = product?.totalReviews ?? 0;

  const formatPrice = (value) =>
    Number(value ?? 0).toLocaleString('fr-FR');

  return (
    <article className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md sm:hover:shadow-lg transition-shadow h-full flex flex-col overflow-hidden">
      {/* Image — hauteur fixe sur tous les écrans (alignement des cartes en grille) */}
      <div className="relative h-36 sm:h-40 md:h-44 lg:h-48 shrink-0">
        <Link to={`/product/${product.slug}`} className="block h-full">
          <img
            src={getProductImage()}
            alt={product?.name}
            className="w-full h-full object-cover cursor-pointer"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.target.src = PRODUCT_PLACEHOLDER_IMAGE;
            }}
          />
        </Link>
        {onToggleFavorite && (
          <button
            type="button"
            title={
              isLiked
                ? t('product_card.remove_favorite', 'Retirer des favoris')
                : t('product_card.add_favorite', 'Ajouter aux favoris')
            }
            onClick={() => handleToggleFavorite(isLiked)}
            className={`absolute top-1.5 right-1.5 sm:top-2 sm:right-2 p-1.5 sm:p-2 rounded-full shadow-md transition-colors ${
              isLiked
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-white/95 hover:bg-white text-gray-700'
            }`}
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill={isLiked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="p-2 sm:p-3 md:p-4 flex flex-col flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs text-gray-400 font-medium truncate mb-0.5 sm:mb-1">
          {product?.category?.name || t('product_card.uncategorized', 'Non catégorisé')}
        </p>

        <Link to={`/product/${product.slug}`} className="block min-w-0">
          <h3 className="text-xs sm:text-sm md:text-base font-semibold text-gray-900 line-clamp-2 leading-snug hover:text-orange-hover transition-colors">
            {product?.name}
          </h3>
        </Link>

        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 mt-1 sm:mt-2">
          {hasActivePromo && (
            <span className="text-[10px] sm:text-xs text-gray-400 line-through whitespace-nowrap">
              {formatPrice(product?.basePrice)} F
            </span>
          )}
          <span className="text-sm sm:text-base md:text-lg font-bold text-orange-hover whitespace-nowrap">
            {formatPrice(displayPrice)} Fcfa
          </span>
        </div>

        <div className="flex items-center gap-1 mt-1 sm:mt-2 min-h-[14px] sm:min-h-[20px]">
          <StarRating
            rating={product?.averageRating}
            size="sm"
            className="shrink-0 [&_svg]:!w-3 [&_svg]:!h-3 sm:[&_svg]:!w-4 sm:[&_svg]:!h-4"
          />
          {reviewCount > 0 && (
            <span className="text-[10px] sm:text-xs text-gray-500 truncate">
              <span className="sm:hidden">({reviewCount})</span>
              <span className="hidden sm:inline">
                ({reviewCount} {t('product_card.reviews', 'avis')})
              </span>
            </span>
          )}
        </div>

        {product?.shop?.shop_name && (
          <p className="text-[10px] sm:text-xs text-gray-600 font-medium truncate mt-1 sm:mt-2 mb-2 sm:mb-3 underline decoration-gray-300">
            {product.shop.shop_name}
          </p>
        )}

        <button
          type="button"
          onClick={handleAddToCart}
          className="mt-auto w-full py-1.5 sm:py-2 px-2 sm:px-4 rounded-full text-[11px] sm:text-sm font-medium transition-colors flex items-center justify-center gap-1 sm:gap-2 bg-white text-gray-900 border border-gray-800 hover:bg-orange-hover hover:text-white hover:border-orange-hover"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 21.283 21.283"
            className="w-4 h-4 sm:w-5 sm:h-5 shrink-0"
            aria-hidden
          >
            <g transform="translate(0.75 0.75)">
              <path
                d="M1148.968,58.484A1.484,1.484,0,1,1,1147.484,57,1.483,1.483,0,0,1,1148.968,58.484Z"
                transform="translate(-1140.064 -40.184)"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M1157.968,58.484A1.484,1.484,0,1,1,1156.484,57,1.483,1.483,0,0,1,1157.968,58.484Z"
                transform="translate(-1140.162 -40.184)"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M1143.957,41.978h11.87a3.956,3.956,0,0,1,3.957,3.957v4.946a3.956,3.956,0,0,1-3.957,3.957h-7.913a3.956,3.956,0,0,1-3.956-3.957Zm0,0A1.981,1.981,0,0,0,1141.978,40H1140m3.957,5.935h15.332"
                transform="translate(-1140 -40)"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </g>
          </svg>
          <span className="truncate sm:hidden">{t('product_card.add_short', 'Panier')}</span>
          <span className="truncate hidden sm:inline">
            {t('add_to_cart', 'Ajouter au panier')}
          </span>
        </button>
      </div>
    </article>
  );
};

export default ProductCard;
